import axios from "../../axios";
import { api } from "../../api";

const URL = "/program-feedback";

export type PentesterFeedbackItem = {
  pentester_user_id: number;
  pentester_username?: string;
  technical_depth_rating: number; // 1-5
  accuracy_rating: number; // 1-5 (accuracy and signal to noise)
  communication_rating: number; // 1-5 (communication and collaboration)
  business_context_rating: number; // 1-5 (business context understanding)
  comments?: string; // Optional comments for this pentester
};

export type ProgramFeedbackSubmission = {
  program_id: number;
  run_id?: number;
  overall_program_rating: number; // 1-5
  overall_program_comments?: string;
  pentester_feedbacks?: PentesterFeedbackItem[];
  is_skipped?: boolean;
  improvement_reasons?: string[];
  improvement_details?: string;
  next_step_option?: string;
  areas_needing_review?: string;
  requested_deadline?: string;
};

export type ProgramNeedingFeedback = {
  feedback_id?: number;
  run_id?: number;
  program_id: number;
  program_title: string;
  run_title?: string;
  expected_end_date: string;
  pentesters: {
    user_id: number;
    username: string;
    display_name: string;
    pfp: string;
  }[];
};

export type FeedbackStatus = {
  has_submitted: boolean;
  is_requested: boolean;
  is_skipped: boolean;
  feedback?: {
    feedback_id: number;
    program_id: number;
    run_id?: number;
    status: string;
    business_user_id: number;
    overall_program_rating: number;
    overall_program_comments?: string;
    pentester_feedbacks: PentesterFeedbackItem[];
    is_skipped: boolean;
    improvement_reasons?: string[];
    improvement_details?: string;
    next_step_option?: string;
    areas_needing_review?: string;
    requested_deadline?: string | null;
    createdAt: string;
    updatedAt: string;
  };
  eligible_pentesters?: {
    user_id: number;
    username: string;
    display_name: string;
    pfp: string;
  }[];
  run?: {
    run_id: number;
    run_title: string;
  };
};

// Submit program feedback
export const submitProgramFeedback = async (
  feedbackData: ProgramFeedbackSubmission
) => {
  const response = await axios.post(`${URL}/submit`, feedbackData);
  return response.data;
};

// Get feedback status for a specific program
export const getFeedbackStatus = async (programId: number, runId?: number) => {
  const response = await axios.get(`${URL}/status`, {
    params: { program_id: programId, run_id: runId }
  });
  return response.data;
};

// Get list of programs that need feedback
export const getProgramsNeedingFeedback = async () => {
  const response = await axios.get(`${URL}/programs-needing-feedback`);
  return response.data;
};

// RTK Query endpoints
export const programFeedbackApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getFeedbackStatus: builder.query<FeedbackStatus, { programId: number, runId?: number }>({
      query: ({ programId, runId }) => ({
        url: `${URL}/status`,
        method: "get",
        params: { program_id: programId, run_id: runId }
      }),
      providesTags: (result, error, { programId }) => [
        { type: "ProgramFeedback", id: programId },
        "ProgramFeedback"
      ]
    }),
    getProgramsNeedingFeedback: builder.query<ProgramNeedingFeedback[], void>({
      query: () => ({
        url: `${URL}/programs-needing-feedback`,
        method: "get"
      }),
      transformResponse: (response: { programs: ProgramNeedingFeedback[] }) => response.programs,
      providesTags: ["ProgramFeedback"]
    }),
    submitFeedback: builder.mutation<any, ProgramFeedbackSubmission>({
      query: (data) => ({
        url: `${URL}/submit`,
        method: "post",
        data
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ProgramFeedback", id: arg.program_id },
        "ProgramFeedback"
      ]
    })
  })
});

export const { useGetFeedbackStatusQuery, useGetProgramsNeedingFeedbackQuery, useSubmitFeedbackMutation } = programFeedbackApi;
